/* eslint-disable dot-notation */
import {Component, Injector, OnInit} from '@angular/core';
import {NGXLogger} from 'ngx-logger';
import {ActivatedRoute, Router} from '@angular/router';
import {SessionObject} from '@twpub/core/models';
import {ClientConfigurationService, SharedStateService} from '@twpub/core/services';
import {ClientConfiguration, ComponentConfiguration} from '@twpub/core/utils';
import {take} from 'rxjs';
import {CssService} from '@twpub/core/services/css.service';

/**
 * Base class for pages holding session object.
 */
@Component({
  template: '',
  standalone: false
})
export abstract class BasePageComponent implements OnInit {
  logger: NGXLogger;
  config?: ComponentConfiguration<any>;
  clientConfig?: ClientConfiguration;
  isConfigurationOk: boolean = false;
  protected route: ActivatedRoute;
  private clientConfigService: ClientConfigurationService;
  private router: Router;
  private sharedStateService: SharedStateService;
  private cssService: CssService;

  get sessionObj(): SessionObject {
    return new SessionObject(this.sharedStateService.sessionObj);
  }

  protected constructor(injector: Injector) {
    this.logger = injector.get(NGXLogger);
    this.router = injector.get(Router);
    this.route = injector.get(ActivatedRoute);
    this.sharedStateService = injector.get(SharedStateService);
    this.clientConfigService = injector.get(ClientConfigurationService);
    this.cssService = injector.get(CssService);
  }

  ngOnInit() {
    this.logger.trace(`BasePageComponent.ngOnInit started for ${this.constructor.name} at ${this.router.url}`);
    this.route.data.pipe(take(1)).subscribe({
      next: (config) => {
        this.clientConfig = config['clientConfig'];
        if (this.clientConfig) {
          this.logger.trace('BasePageComponent.ngOnInit updateCss:', {configId: this.clientConfig.id})
          this.cssService.updateConfiguration(this.clientConfig);
        }
      }
    });

    this.clientConfigService.hasTermWebConfiguration().pipe(take(1)).subscribe({
      next: () => {
        this.isConfigurationOk = true;
        this.addClientCss();
      },
      error: (err) => {
        this.logger.error(`BasePageComponent.hasTermWebConfiguration failed for ${this.constructor.name}`, err);
        this.isConfigurationOk = false;
        this.router.navigate(['/admin/home']);
      }
    });
  }

  public addClientCss() {
    const css = this.clientConfig?.css;
    if (css) {
      const prefixedCss = this.prefixCssClasses(css, '.twpub-config-' + this.clientConfig?.id);
      const head = document.getElementsByTagName('head')[0];
      let style = document.getElementById('clientConfigCss');
      if (!style) {
        style = document.createElement('style');
        style.setAttribute('id', 'clientConfigCss');
        head.appendChild(style);
      } else {
        this.logger.warn('BasePageComponent.addClientCss: CSS already applied');
        while (style.firstChild) {
          style.removeChild(style.firstChild);
        }
      }
      style.appendChild(document.createTextNode(prefixedCss));
    }
  }

  public prefixCssClasses(css: string, prefix: string): string {
    let result = '';
    let currentStatement = '';
    let braceCount = 0;

    // Split lines and preserve newlines
    const lines = css.split('\n');

    for (const line of lines) {
      const trimmedLine = line.trim();

      // Preserve empty lines and comments
      if (!trimmedLine) {
        result += line + '\n';
        continue;
      }
      if (trimmedLine.startsWith('/*') || trimmedLine.startsWith('*/') || trimmedLine.includes('*/')) {
        result += line + '\n';
        continue;
      }

      currentStatement += line + '\n';

      // Count braces to track statement boundaries
      if (trimmedLine.includes('{')) {
        braceCount += trimmedLine.match(/{/g)?.length || 0;
      }
      if (trimmedLine.includes('}')) {
        braceCount -= trimmedLine.match(/}/g)?.length || 0;
      }

      // Process statement when block is complete
      if (braceCount === 0 && currentStatement.trim()) {
        const prefixedStatement = this.addPrefixToStatement(currentStatement, prefix);
        result += prefixedStatement;
        currentStatement = '';
      }
    }

    // Handle any remaining statement
    if (currentStatement.trim()) {
      result += this.addPrefixToStatement(currentStatement, prefix);
    }

    return result;
  }

  private addPrefixToStatement(statement: string, prefix: string): string {
    const selectorEndIndex = statement.indexOf('{');
    if (selectorEndIndex === -1) {
      return statement; // Not a ruleset, return as-is
    }

    const selectors = statement.substring(0, selectorEndIndex).split(',').map(s => s.trim());
    const body = statement.substring(selectorEndIndex);

    // Prepend prefix to all selectors
    const prefixedSelectors = selectors.map((selector) => {
      if (!selector) {
        return selector;
      }
      return `${prefix} ${selector}`;
    });

    return prefixedSelectors.join(', ') + body;
  }
}
